import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './CaseChat.css';
import ChatHeader from '../components/ChatHeader';
import MessageBubble from '../components/MessageBubble';
import ChatInput from '../components/ChatInput';

const CaseChat = () => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [chat, setChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const pollingIntervalRef = useRef(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  // Get auth token
  const getToken = () => localStorage.getItem('token');

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Fetch or create chat for this case
  const fetchOrCreateChat = async () => {
    try {
      const token = getToken();
      if (!token) {
        navigate('/login');
        return;
      }

      // Try to get existing chat
      let response;
      try {
        response = await axios.get(
          `${BACKEND_URL}/api/chats/case/${caseId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setChat(response.data.chat);
      } catch (err) {
        // If chat doesn't exist, create it
        if (err.response?.status === 404) {
          const createResponse = await axios.post(
            `${BACKEND_URL}/api/chats`,
            { case_id: caseId, participants: [] },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setChat(createResponse.data.chat);
        } else {
          throw err;
        }
      }
    } catch (err) {
      console.error('Error fetching/creating chat:', err);
      setError('Failed to load chat. Please try again.');
    }
  };

  // Fetch messages
  const fetchMessages = async (chatId) => {
    try {
      const token = getToken();
      if (!token || !chatId) return;

      const response = await axios.get(
        `${BACKEND_URL}/api/chats/${chatId}/messages?limit=50`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setMessages(response.data.messages || []);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching messages:', err);
      if (err.response?.status === 401) {
        navigate('/login');
      }
    }
  };

  // Send message
  const handleSendMessage = async (content, messageType = 'text', metadata = null) => {
    if (!chat || !content.trim()) return;

    setSending(true);
    try {
      const token = getToken();
      const response = await axios.post(
        `${BACKEND_URL}/api/chats/${chat.chat_id}/messages`,
        {
          content: content.trim(),
          message_type: messageType,
          metadata: metadata
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Add new message to list
      const newMessage = response.data.message;
      setMessages(prev => [...prev, newMessage]);
      
      // Scroll to bottom
      setTimeout(scrollToBottom, 100);
    } catch (err) {
      console.error('Error sending message:', err);
      alert('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  // Upload file
  const handleFileUpload = async (file) => {
    if (!chat || !file) return;

    setSending(true);
    try {
      const token = getToken();
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${BACKEND_URL}/api/chats/${chat.chat_id}/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      // Add file message to list
      const newMessage = response.data.message;
      setMessages(prev => [...prev, newMessage]);
      
      // Scroll to bottom
      setTimeout(scrollToBottom, 100);
      
      return response.data;
    } catch (err) {
      console.error('Error uploading file:', err);
      alert('Failed to upload file. Please try again.');
      throw err;
    } finally {
      setSending(false);
    }
  };

  // Initialize chat
  useEffect(() => {
    fetchOrCreateChat();
  }, [caseId]);

  // Fetch messages when chat is loaded
  useEffect(() => {
    if (chat?.chat_id) {
      fetchMessages(chat.chat_id);
      scrollToBottom();
    }
  }, [chat?.chat_id]);

  // Set up polling for new messages (every 3 seconds)
  useEffect(() => {
    if (chat?.chat_id) {
      pollingIntervalRef.current = setInterval(() => {
        fetchMessages(chat.chat_id);
      }, 3000);

      return () => {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
        }
      };
    }
  }, [chat?.chat_id]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  if (error) {
    return (
      <div className="case-chat-error">
        <div className="error-content">
          <h2>⚠️ Error</h2>
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="case-chat-loading">
        <div className="loading-spinner"></div>
        <p>Loading chat...</p>
      </div>
    );
  }

  return (
    <div className="case-chat-container">
      <ChatHeader 
        caseId={caseId}
        status={chat?.status || 'active'}
        tenantName={chat?.tenant_name}
        bankName={chat?.bank_display_name}
        userEmail={chat?.created_by_email}
        onBack={() => navigate(-1)}
      />

      <div className="messages-container">
        {messages.length === 0 ? (
          <div className="no-messages">
            <p>No messages yet. Start the conversation!</p>
          </div>
        ) : (
          messages.map((message) => (
            <MessageBubble key={message.message_id} message={message} />
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      <ChatInput
        onSendMessage={handleSendMessage}
        onFileUpload={handleFileUpload}
        disabled={sending}
      />
    </div>
  );
};

export default CaseChat;
